import { useAuth } from "@/lib/auth";
import { Navigate, useLocation } from "react-router-dom";
import { ReactNode } from "react";
import type { UserRole } from "@workspace/api-client";
import { useFeatureFlagsContext } from "@/contexts/FeatureFlagsContext";
import { ROLE_PERMISSIONS } from "@/lib/role-permissions";
import { useRolePermissions } from "@/lib/api-extra";
import type { AnyRole } from "@/lib/roles";

interface AuthGuardProps {
  children: ReactNode;
  allowedRoles?: UserRole[];
  /** Permission string the user must have (e.g. "vehicles:write"). Superadmin bypasses this check. */
  requiredPermission?: string;
  requiredFlag?: string;
}

export function AuthGuard({ children, allowedRoles, requiredPermission, requiredFlag }: AuthGuardProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { enabledFlags, isLoading: flagsLoading } = useFeatureFlagsContext();
  const { data: rolePermsData } = useRolePermissions();
  const location = useLocation();

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center">Cargando...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (user?.mustChangePassword) {
    return <Navigate to="/cambiar-acceso" replace />;
  }

  if (allowedRoles && user && user.rol !== "superadmin" && !allowedRoles.includes(user.rol as UserRole)) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 text-muted-foreground p-8">
        <p className="text-lg font-semibold">Acceso restringido</p>
        <p className="text-sm">No tiene permisos para acceder a esta sección.</p>
      </div>
    );
  }

  if (requiredPermission && user && user.rol !== "superadmin") {
    const dynamicPerms = rolePermsData?.data?.[user.rol as AnyRole];
    const userPerms: readonly string[] = dynamicPerms ?? ROLE_PERMISSIONS[user.rol as AnyRole] ?? [];
    if (!userPerms.includes("*") && !userPerms.includes(requiredPermission)) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-2 text-muted-foreground p-8">
          <p className="text-lg font-semibold">Acceso restringido</p>
          <p className="text-sm">No tiene permisos para acceder a esta sección.</p>
        </div>
      );
    }
  }

  if (!flagsLoading && requiredFlag && !enabledFlags.has(requiredFlag)) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 text-muted-foreground p-8">
        <p className="text-lg font-semibold">Módulo desactivado</p>
        <p className="text-sm">Esta funcionalidad no está habilitada en este momento.</p>
      </div>
    );
  }

  return <>{children}</>;
}
